import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ShoppingCart as SellIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';
import { Item } from '../models/Item';
import BulkImportDialog from './BulkImportDialog';
import BulkEditDialog from './BulkEditDialog';
import EditItemDialog from './EditItemDialog';
import { getCategoriesForShop, getDefaultCategoryForShop } from '../utils/categories';

const InventorySpreadsheet = forwardRef(({ shopName }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sellDialog, setSellDialog] = useState({ open: false, item: null, quantity: 1, sellingPrice: '' });
  const [bulkImportDialog, setBulkImportDialog] = useState(false);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printOptions, setPrintOptions] = useState({
    brand: true,
    name: true,
    description: true,
    quantity: true,
    costing: true,
    overallTotal: true,
    alphabetical: true,
    inStockOnly: true,
  });
  // custom category for inline category editing
  const [inlineCustomCategory, setInlineCustomCategory] = useState({ itemId: null, value: '' });

  const predefinedCategories = getCategoriesForShop(shopName);

  // Merge predefined categories with any custom categories that exist in the items
  const allCategories = useMemo(() => {
    const customCats = items
      .map(item => item.category)
      .filter(cat => cat && !predefinedCategories.includes(cat));
    const uniqueCustom = [...new Set(customCats)];
    return [...predefinedCategories, ...uniqueCustom.sort()];
  }, [items, predefinedCategories]);

  // Load items
  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const allItems = await inventoryService.getAllItems(shopName);
      setItems(allItems);
      setFilteredItems(allItems);
    } catch (error) {
      showSnackbar('Error loading items', 'error');
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  }, [shopName]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Filter items based on search and category
  useEffect(() => {
    let filtered = items;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(term) ||
        item.brand.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    setFilteredItems(filtered);
  }, [items, searchTerm, selectedCategory]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatCurrency = (value) => `PHP ${(Number(value) || 0).toFixed(2)}`;

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const exportInventoryPdf = () => {
    if (filteredItems.length === 0) {
      showSnackbar('There are no inventory items to print', 'warning');
      return;
    }

    const selectedColumns = [
      printOptions.brand && { key: 'brand', label: 'Brand' },
      printOptions.name && { key: 'name', label: 'Name' },
      printOptions.description && { key: 'description', label: 'Description' },
      printOptions.quantity && { key: 'quantity', label: 'Quantity', numeric: true },
      printOptions.costing && { key: 'costing', label: 'Costing', numeric: true },
    ].filter(Boolean);

    if (selectedColumns.length === 0 && !printOptions.overallTotal) {
      showSnackbar('Select at least one field to print', 'warning');
      return;
    }

    const quantityFilteredItems = printOptions.inStockOnly
      ? filteredItems.filter((item) => Number(item.quantity) > 0)
      : filteredItems;

    if (quantityFilteredItems.length === 0) {
      showSnackbar('There are no products with quantity to print', 'warning');
      return;
    }

    const exportItems = printOptions.alphabetical
      ? [...quantityFilteredItems].sort((left, right) => {
          const brandComparison = String(left.brand || '').localeCompare(String(right.brand || ''), 'en', {
            sensitivity: 'base',
            numeric: true,
          });

          if (brandComparison !== 0) {
            return brandComparison;
          }

          return String(left.name || '').localeCompare(String(right.name || ''), 'en', {
            sensitivity: 'base',
            numeric: true,
          });
        })
      : quantityFilteredItems;

    const totalQuantity = exportItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const totalCost = exportItems.reduce((sum, item) => {
      return sum + ((Number(item.quantity) || 0) * (Number(item.originalCost) || 0));
    }, 0);
    const cellValue = (item, key) => {
      switch (key) {
        case 'brand': return item.brand || '';
        case 'name': return item.name || '';
        case 'description': return item.description || '';
        case 'quantity': return Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : '';
        case 'costing': return formatCurrency(item.originalCost);
        default: return '';
      }
    };

    const headers = selectedColumns.map(column => `<th class="${column.numeric ? 'numeric' : ''}">${column.label}</th>`).join('');
    const rows = exportItems.map(item => {
      const cells = selectedColumns.map(column => {
        const value = escapeHtml(cellValue(item, column.key));
        return `<td class="${column.numeric ? 'numeric' : ''}">${value || '&nbsp;'}</td>`;
      }).join('');
      return `<tr>${cells || '<td>&nbsp;</td>'}</tr>`;
    }).join('');

    const reportFileName = `${String(shopName || 'Inventory')
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '') || 'Inventory'}_Report_${new Date().toISOString().split('T')[0]}.pdf`;

    const totals = printOptions.overallTotal ? `
      <section class="summary">
        <h2>Overall Total</h2>
        <div class="summary-grid">
          <div><span>Items</span><strong>${exportItems.length}</strong></div>
          ${printOptions.quantity ? `<div><span>Quantity</span><strong>${totalQuantity}</strong></div>` : ''}
          ${printOptions.costing ? `<div><span>Inventory Cost</span><strong>${escapeHtml(formatCurrency(totalCost))}</strong></div>` : ''}
        </div>
      </section>` : '';

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      showSnackbar('Please allow pop-ups to print the report', 'error');
      return;
    }

    printWindow.document.write(`<!doctype html>
      <html>
        <head>
          <title>${escapeHtml(reportFileName)}</title>
          <style>
            @page { size: auto; margin: 14mm; }
            * { box-sizing: border-box; }
            body { margin: 0; color: #111827; font-family: Arial, sans-serif; font-size: 12px; }
            header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; }
            h1 { margin: 0 0 5px; font-size: 22px; }
            p { margin: 0; color: #4b5563; }
            .date { text-align: right; white-space: nowrap; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            th, td { border: 1px solid #d1d5db; padding: 7px 8px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
            th { background: #f3f4f6; font-weight: 700; }
            .numeric { text-align: right; white-space: nowrap; }
            tbody tr:nth-child(even) { background: #f9fafb; }
            .summary { margin-top: 18px; border-top: 2px solid #111827; padding-top: 10px; }
            .summary h2 { margin: 0 0 8px; font-size: 15px; }
            .summary-grid { display: flex; flex-wrap: wrap; gap: 24px; }
            .summary-grid div { min-width: 120px; }
            .summary-grid span, .summary-grid strong { display: block; }
            .summary-grid span { color: #4b5563; margin-bottom: 3px; }
            .summary-grid strong { font-size: 14px; }
            @media print { .summary { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <header>
            <div>
              <h1>${escapeHtml(shopName)} Inventory Report</h1>
              <p>${exportItems.length} item${exportItems.length === 1 ? '' : 's'}${selectedCategory ? ` · Category: ${escapeHtml(selectedCategory)}` : ''}${printOptions.alphabetical ? ' · Sorted by Brand (A–Z)' : ''}</p>
            </div>
            <p class="date">Printed ${escapeHtml(new Date().toLocaleString('en-PH'))}</p>
          </header>
          <table>
            <thead><tr>${headers || '<th>Inventory</th>'}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
          ${totals}
        </body>
      </html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    setPrintDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Cell editing functions
  const startEditing = (rowIndex, field, value) => {
    setEditingCell({ rowIndex, field });
    setEditValue(value || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEditing = async () => {
    if (!editingCell) return;

    try {
      const { rowIndex, field } = editingCell;
      const item = filteredItems[rowIndex];
      const updatedItem = new Item({ ...item });

      // Update the field
      if (field === 'quantity' || field === 'sellingPrice' || field === 'originalCost') {
        updatedItem[field] = parseFloat(editValue) || 0;
      } else {
        updatedItem[field] = editValue;
      }

      await inventoryService.updateItem(updatedItem);

      // Update local state
      const updatedItems = [...items];
      const originalIndex = items.findIndex(i => i.id === item.id);
      updatedItems[originalIndex] = updatedItem;
      setItems(updatedItems);

      showSnackbar('Item updated successfully');
      cancelEditing();
    } catch (error) {
      showSnackbar('Error updating item', 'error');
      console.error('Error updating item:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEditing();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Add new item
  const addNewItem = async () => {
    try {
      const newItem = new Item({
        shopName,
        name: 'New Item',
        brand: '',
        category: getDefaultCategoryForShop(shopName),
        quantity: 0,
        sellingPrice: 0,
        originalCost: 0
      });

      const id = await inventoryService.insertItem(newItem);
      newItem.id = id;

      setItems([newItem, ...items]);
      showSnackbar('New item added');
    } catch (error) {
      showSnackbar('Error adding item', 'error');
      console.error('Error adding item:', error);
    }
  };

  // Expose addNewItem to parent component via ref
  useImperativeHandle(ref, () => ({
    addNewItem
  }));

  // Delete item
  const deleteItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    try {
      await inventoryService.deleteItem(item);
      setItems(items.filter(i => i.id !== item.id));
      showSnackbar('Item deleted successfully');
    } catch (error) {
      showSnackbar('Error deleting item', 'error');
      console.error('Error deleting item:', error);
    }
  };

  // Sell item
  const openSellDialog = (item) => {
    setSellDialog({ open: true, item, quantity: 1 });
  };

  const closeSellDialog = () => {
    setSellDialog({ open: false, item: null, quantity: 1, sellingPrice: '' });
  };

  const sellItem = async () => {
    try {
      const { item, quantity, sellingPrice } = sellDialog;

      // If item has no selling price, require user to enter one
      const priceToUse = item.sellingPrice || parseFloat(sellingPrice);
      if (!priceToUse || priceToUse <= 0) {
        showSnackbar('Please enter a selling price', 'error');
        return;
      }

      await inventoryService.sellItem(item, quantity, 0, priceToUse);

      // Update local state
      const updatedItems = items.map(i =>
        i.id === item.id ? { ...i, quantity: i.quantity - quantity } : i
      );
      setItems(updatedItems);

      showSnackbar(`Sold ${quantity} unit(s) of ${item.name}`);
      closeSellDialog();
    } catch (error) {
      showSnackbar(error.message || 'Error selling item', 'error');
      console.error('Error selling item:', error);
    }
  };

  const handleBulkImport = async (importedItems) => {
    try {
      await inventoryService.bulkInsertItems(importedItems);
      await loadItems(); // Reload items to show the new ones
      showSnackbar(`Successfully imported ${importedItems.length} item(s)`, 'success');
    } catch (error) {
      showSnackbar('Error importing items', 'error');
      console.error('Error importing items:', error);
      throw error;
    }
  };

  // Handle category change — supports "Other" → custom input
  const handleCategoryChange = async (item, newCategory) => {
    if (newCategory === 'Other') {
      // Show inline custom category input
      setInlineCustomCategory({ itemId: item.id, value: '' });
      return;
    }

    // Normal category update
    const updatedItem = new Item({ ...item, category: newCategory });
    try {
      await inventoryService.updateItem(updatedItem);
      const updatedItems = items.map(i =>
        i.id === item.id ? updatedItem : i
      );
      setItems(updatedItems);
      showSnackbar('Category updated');
    } catch (error) {
      showSnackbar('Error updating category', 'error');
    }
  };

  // Save custom category from inline editing
  const saveInlineCustomCategory = async (item) => {
    const customValue = inlineCustomCategory.value.trim();
    const categoryToSave = customValue || 'Other';
    const updatedItem = new Item({ ...item, category: categoryToSave });
    try {
      await inventoryService.updateItem(updatedItem);
      const updatedItems = items.map(i =>
        i.id === item.id ? updatedItem : i
      );
      setItems(updatedItems);
      showSnackbar(`Category set to "${categoryToSave}"`);
      setInlineCustomCategory({ itemId: null, value: '' });
    } catch (error) {
      showSnackbar('Error updating category', 'error');
    }
  };

  const renderCategoryEditor = (item) => {
    if (inlineCustomCategory.itemId === item.id) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Type category name..."
            value={inlineCustomCategory.value}
            onChange={(e) => setInlineCustomCategory({ ...inlineCustomCategory, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveInlineCustomCategory(item);
              if (e.key === 'Escape') setInlineCustomCategory({ itemId: null, value: '' });
            }}
            autoFocus
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={() => saveInlineCustomCategory(item)} color="primary" sx={{ padding: 0.5, minHeight: 44, minWidth: 44 }}>
              <SaveIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
            <IconButton size="small" onClick={() => setInlineCustomCategory({ itemId: null, value: '' })} sx={{ padding: 0.5, color: '#94a3b8', minHeight: 44, minWidth: 44 }}>
              <CancelIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>
        </Box>
      );
    }

    return (
      <Select
        size="small"
        value={predefinedCategories.includes(item.category) ? item.category : 'Other'}
        onChange={(e) => handleCategoryChange(item, e.target.value)}
        fullWidth
        sx={{
          fontSize: { xs: '0.75rem', sm: '0.8rem' },
          '& .MuiSelect-select': { py: 0.75 },
        }}
      >
        {predefinedCategories.map(category => (
          <MenuItem key={category} value={category} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
            {category}
          </MenuItem>
        ))}
        {!predefinedCategories.includes(item.category) && item.category !== 'Other' && (
          <MenuItem value={item.category} sx={{
            fontSize: { xs: '0.75rem', sm: '0.85rem' },
            fontStyle: 'italic', color: '#E53935',
          }}>
            {item.category} (custom)
          </MenuItem>
        )}
        <MenuItem value="Other" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
          Other / Custom...
        </MenuItem>
      </Select>
    );
  };

  const renderInventoryStatus = (item) => {
    if (item.quantity <= 0) {
      return (
        <Chip
          label="Out of stock"
          size="small"
          sx={{
            fontSize: '0.65rem', height: 24, fontWeight: 700,
            background: 'rgba(239,68,68,0.1)',
            color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)',
          }}
        />
      );
    }

    if (item.quantity <= 5) {
      return (
        <Chip
          label="Low stock"
          size="small"
          sx={{
            fontSize: '0.65rem', height: 24, fontWeight: 700,
            background: 'rgba(245,158,11,0.12)',
            color: '#d97706', border: '1px solid rgba(245,158,11,0.2)',
          }}
        />
      );
    }

    return (
      <Chip
        label="Ready"
        size="small"
        sx={{
          fontSize: '0.65rem', height: 24, fontWeight: 700,
          background: 'rgba(16,185,129,0.1)',
          color: '#059669', border: '1px solid rgba(16,185,129,0.15)',
        }}
      />
    );
  };

  const renderCell = (item, field, value, rowIndex) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            fullWidth
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: { xs: 100, sm: 150 } }}
          />
          <IconButton size="small" onClick={saveEditing} color="primary" sx={{ padding: { xs: 0.5, sm: 1 }, minHeight: 44, minWidth: 44 }}>
            <SaveIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
          </IconButton>
          <IconButton size="small" onClick={cancelEditing} color="secondary" sx={{ padding: { xs: 0.5, sm: 1 }, minHeight: 44, minWidth: 44 }}>
            <CancelIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box
        onClick={() => startEditing(rowIndex, field, value)}
        sx={{
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(229, 57, 53, 0.06)', },
          padding: { xs: '4px 6px', sm: '6px 10px' },
          borderRadius: '8px',
          minHeight: { xs: '28px', sm: '36px' },
          display: 'flex',
          alignItems: 'center',
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          transition: 'background 0.15s ease',
        }}
      >
        {field === 'sellingPrice' || field === 'originalCost' ?
          `₱${(value || 0).toFixed(2)}` :
          value || ''
        }
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <InventoryIcon sx={{ fontSize: 28, color: '#F5F5F5' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#F5F5F5' }}>
            Loading inventory...
          </Typography>
        </Box>
        <LinearProgress sx={{ borderRadius: 2, height: 4 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2.5, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: '#E53935',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(229,57,53,0.25)',
          }}>
            <InventoryIcon sx={{ fontSize: 22, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 800, color: '#F5F5F5',
              fontSize: { xs: '1.15rem', sm: '1.5rem' },
              letterSpacing: '-0.02em',
            }}>
              {shopName} Inventory
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
              {items.length} items • {items.reduce((s, i) => s + (Number(i.quantity) || 0), 0)} units in stock
            </Typography>
          </Box>
        </Box>
        <Box sx={{
          display: 'flex', gap: { xs: 1, sm: 1.5 },
          flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setBulkImportDialog(true)}
            size="small"
            sx={{
              fontSize: { xs: '0.72rem', sm: '0.8rem' },
              flex: { xs: '1 1 auto', sm: 'none' },
              minWidth: { xs: 'auto', sm: 110 },
              minHeight: 44,
              borderColor: 'rgba(255,255,255,0.08)',
              color: '#F5F5F5',
              '&:hover': { borderColor: '#E53935', background: 'rgba(229,57,53,0.04)' },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bulk Import</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Import</Box>
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setBulkEditDialog(true)}
            size="small"
            sx={{
              fontSize: { xs: '0.72rem', sm: '0.8rem' },
              flex: { xs: '1 1 auto', sm: 'none' },
              minWidth: { xs: 'auto', sm: 110 },
              minHeight: 44,
              borderColor: 'rgba(229,57,53,0.3)',
              color: '#E53935',
              '&:hover': { borderColor: '#E53935', background: 'rgba(229,57,53,0.04)' },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bulk Edit</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Edit</Box>
          </Button>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={() => setPrintDialogOpen(true)}
            size="small"
            sx={{
              fontSize: { xs: '0.72rem', sm: '0.8rem' },
              flex: { xs: '1 1 auto', sm: 'none' },
              minWidth: { xs: 'auto', sm: 110 },
              minHeight: 44,
              borderColor: 'rgba(255,255,255,0.18)',
              color: '#F5F5F5',
              '&:hover': { borderColor: '#E53935', background: 'rgba(229,57,53,0.04)' },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export PDF</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Export</Box>
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addNewItem}
            size="small"
            sx={{
              fontSize: { xs: '0.72rem', sm: '0.8rem' },
              flex: { xs: '1 1 auto', sm: 'none' },
              minWidth: { xs: 'auto', sm: 110 },
              minHeight: 44,
              background: '#E53935',
              '&:hover': {
                background: '#C62828',
                boxShadow: '0 4px 14px rgba(229,57,53,0.3)',
              },
            }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{
        display: 'flex', gap: { xs: 1.5, sm: 2 },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          size="small"
          placeholder="Search items..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} />,
            ...{ sx: { fontSize: { xs: '16px', sm: '0.875rem' } } }
          }}
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
        />

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, '& .MuiSelect-select': { fontSize: { xs: '16px', sm: '0.875rem' } } }}>
          <InputLabel sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="">All Categories</MenuItem>
            {allCategories.map(category => (
              <MenuItem key={category} value={category}>{category}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Spreadsheet */}
      {isMobile && (
        <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
          {filteredItems.map((item) => (
            <Card
              key={item.id}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: '#F5F5F5', fontSize: '1rem', lineHeight: 1.25 }}>
                      {item.name}
                    </Typography>
                    <Typography sx={{ color: '#BDBDBD', fontSize: '0.76rem', mt: 0.4 }}>
                      {item.brand || 'No brand'} • {item.category || 'Other'}
                    </Typography>
                  </Box>
                  {renderInventoryStatus(item)}
                </Box>

                {item.description && (
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.78rem', lineHeight: 1.55, mb: 1.5 }}>
                    {item.description}
                  </Typography>
                )}

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                  mb: 1.5
                }}>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#3A3A3A' }}>
                    <Typography sx={{ color: '#BDBDBD', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      Quantity
                    </Typography>
                    <Typography sx={{ color: '#F5F5F5', fontWeight: 800, mt: 0.4 }}>
                      {Number(item.quantity) || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#3A3A3A' }}>
                    <Typography sx={{ color: '#BDBDBD', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      Sell Price
                    </Typography>
                    <Typography sx={{ color: '#F5F5F5', fontWeight: 800, mt: 0.4 }}>
                      {formatCurrency(item.sellingPrice)}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#3A3A3A' }}>
                    <Typography sx={{ color: '#BDBDBD', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      Cost
                    </Typography>
                    <Typography sx={{ color: '#F5F5F5', fontWeight: 800, mt: 0.4 }}>
                      {formatCurrency(item.originalCost)}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#3A3A3A' }}>
                    <Typography sx={{ color: '#BDBDBD', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      Shop
                    </Typography>
                    <Typography sx={{ color: '#F5F5F5', fontWeight: 800, mt: 0.4, fontSize: '0.82rem' }}>
                      {item.shopName}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', mb: 0.8 }}>
                    Category
                  </Typography>
                  {renderCategoryEditor(item)}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setEditDialog({ open: true, item })}
                    sx={{ fontSize: '0.72rem', minHeight: 44, borderColor: 'rgba(255,255,255,0.08)', color: '#F5F5F5' }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SellIcon />}
                    onClick={() => openSellDialog(item)}
                    disabled={item.quantity <= 0}
                    sx={{
                      fontSize: '0.72rem', minHeight: 44,
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #059669 100%)' }
                    }}
                  >
                    Sell
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => deleteItem(item)}
                    sx={{ fontSize: '0.72rem', minHeight: 44, borderColor: 'rgba(239,68,68,0.2)', color: '#dc2626' }}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}

          {filteredItems.length === 0 && (
            <Paper elevation={0} sx={{ textAlign: 'center', py: 6, borderRadius: 3, border: '1px dashed rgba(255,255,255,0.12)' }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
              <Typography variant="body1" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                {searchTerm || selectedCategory ? 'No items match your filters' : 'No items in inventory'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mt: 0.5 }}>
                {searchTerm || selectedCategory ? 'Try adjusting your search or category filter' : 'Tap "Add Item" to get started'}
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      <Paper elevation={0} sx={{
        display: { xs: 'none', sm: 'block' },
        width: '100%', overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -1px rgba(0,0,0,0.2)',
        borderRadius: 3,
      }}>
        <TableContainer
          sx={{
            maxHeight: { xs: '60vh', sm: '70vh' },
            overflowX: 'auto',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 80, sm: 100 }
                }}>Actions</TableCell>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 120, sm: 150 }
                }}>Name</TableCell>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', md: 'table-cell' }
                }}>Brand</TableCell>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 100, sm: 140 }
                }}>Category</TableCell>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 150, sm: 200 }, display: { xs: 'none', lg: 'table-cell' }
                }}>Description</TableCell>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 80, sm: 100 }
                }}>Qty</TableCell>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 100, sm: 120 }
                }}>Sell Price</TableCell>
                <TableCell sx={{
                  backgroundColor: '#2D2D2D', color: '#F5F5F5', fontWeight: 700, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 },
                  minWidth: { xs: 100, sm: 120 }
                }}>Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <TableRow hover>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 } }}>
                      <Box sx={{ display: 'flex', gap: { xs: 0, sm: 0.5 } }}>
                        <Tooltip title="Edit Item">
                          <IconButton
                            size="small"
                            onClick={() => setEditDialog({ open: true, item })}
                            sx={{
                              color: '#F5F5F5', padding: { xs: 0.5, sm: 0.75 }, minHeight: 44, minWidth: 44,
                              '&:hover': { background: 'rgba(229,57,53,0.08)' },
                            }}
                          >
                            <EditIcon sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sell Item">
                          <IconButton
                            size="small"
                            onClick={() => openSellDialog(item)}
                            disabled={item.quantity <= 0}
                            sx={{
                              color: '#10b981', padding: { xs: 0.5, sm: 0.75 }, minHeight: 44, minWidth: 44,
                              '&:hover': { background: 'rgba(16,185,129,0.08)' },
                            }}
                          >
                            <SellIcon sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Item">
                          <IconButton
                            size="small"
                            onClick={() => deleteItem(item)}
                            sx={{
                              color: '#ef4444', padding: { xs: 0.5, sm: 0.75 }, minHeight: 44, minWidth: 44,
                              '&:hover': { background: 'rgba(239,68,68,0.08)' },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {renderCell(item, 'name', item.name, index)}
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                      {renderCell(item, 'brand', item.brand, index)}
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 } }}>
                      {inlineCustomCategory.itemId === item.id ? (
                        /* Custom category inline input */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Type category name..."
                            value={inlineCustomCategory.value}
                            onChange={(e) => setInlineCustomCategory({ ...inlineCustomCategory, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveInlineCustomCategory(item);
                              if (e.key === 'Escape') setInlineCustomCategory({ itemId: null, value: '' });
                            }}
                            autoFocus
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          />
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => saveInlineCustomCategory(item)} color="primary" sx={{ padding: 0.5, minHeight: 44, minWidth: 44 }}>
                              <SaveIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => setInlineCustomCategory({ itemId: null, value: '' })} sx={{ padding: 0.5, color: '#94a3b8', minHeight: 44, minWidth: 44 }}>
                              <CancelIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        /* Normal category select — includes "Other" for custom */
                        <Select
                          size="small"
                          value={predefinedCategories.includes(item.category) ? item.category : 'Other'}
                          onChange={(e) => handleCategoryChange(item, e.target.value)}
                          fullWidth
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            '& .MuiSelect-select': { py: 0.75 },
                          }}
                        >
                          {predefinedCategories.map(category => (
                            <MenuItem key={category} value={category} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                              {category}
                            </MenuItem>
                          ))}
                          {/* Show the custom category as an option if it exists */}
                          {!predefinedCategories.includes(item.category) && item.category !== 'Other' && (
                            <MenuItem value={item.category} sx={{
                              fontSize: { xs: '0.75rem', sm: '0.85rem' },
                              fontStyle: 'italic', color: '#E53935',
                            }}>
                              {item.category} (custom)
                            </MenuItem>
                          )}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                      {renderCell(item, 'description', item.description, index)}
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                        {renderCell(item, 'quantity', item.quantity, index)}
                        {item.quantity > 0 && item.quantity <= 5 && (
                          <Chip
                            label="Low"
                            size="small"
                            sx={{
                              fontSize: '0.6rem', height: 20, fontWeight: 700,
                              background: 'rgba(245,158,11,0.12)',
                              color: '#d97706', border: '1px solid rgba(245,158,11,0.2)',
                            }}
                          />
                        )}
                        {item.quantity <= 0 && (
                          <Chip
                            label="Out"
                            size="small"
                            sx={{
                              fontSize: '0.6rem', height: 20, fontWeight: 700,
                              background: 'rgba(239,68,68,0.1)',
                              color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {renderCell(item, 'sellingPrice', item.sellingPrice, index)}
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {renderCell(item, 'originalCost', item.originalCost, index)}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredItems.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <InventoryIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#BDBDBD', fontWeight: 500 }}>
              {searchTerm || selectedCategory ? 'No items match your filters' : 'No items in inventory'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mt: 0.5 }}>
              {searchTerm || selectedCategory ? 'Try adjusting your search or category filter' : 'Click "Add Item" to get started'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Export Inventory PDF Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Export Inventory as PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose the fields to include. The report will use the current search and category filters ({filteredItems.length} item{filteredItems.length === 1 ? '' : 's'}).
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            After clicking Export PDF, choose <strong>Save to PDF</strong> in the browser print window.
          </Typography>
          <FormGroup>
            {[
              ['brand', 'Brand'],
              ['name', 'Name'],
              ['description', 'Description'],
              ['quantity', 'Include Quantity (not empty)'],
              ['costing', 'Include Costing'],
              ['overallTotal', 'Include Overall Total'],
              ['alphabetical', 'Sort alphabetically by Brand (A–Z)'],
              ['inStockOnly', 'Only print products with quantity greater than 0'],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={printOptions[key]}
                    onChange={(event) => setPrintOptions({
                      ...printOptions,
                      [key]: event.target.checked,
                    })}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPrintDialogOpen(false)} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={exportInventoryPdf}
            variant="contained"
            startIcon={<PdfIcon />}
            sx={{
              background: '#E53935',
              '&:hover': { background: '#C62828' },
            }}
          >
            Export PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sell Dialog */}
      <Dialog open={sellDialog.open} onClose={closeSellDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{
          background: '#E53935',
          color: 'white', pb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 38, height: 38, borderRadius: 2,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SellIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Sell Item</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
            {sellDialog.item?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Available: {sellDialog.item?.quantity} units
          </Typography>
          {sellDialog.item?.sellingPrice > 0 ? (
            <Chip
              label={`₱${sellDialog.item.sellingPrice.toFixed(2)} per unit`}
              sx={{
                mb: 2.5, fontWeight: 600,
                background: 'rgba(229,57,53,0.1)', color: '#E53935',
              }}
            />
          ) : (
            <TextField
              label="Selling Price per Unit (₱)"
              type="number"
              fullWidth
              value={sellDialog.sellingPrice}
              onChange={(e) => setSellDialog({
                ...sellDialog,
                sellingPrice: e.target.value
              })}
              inputProps={{ min: 0, step: 0.01, style: { fontSize: '16px' } }}
              required
              sx={{ mb: 2.5 }}
            />
          )}
          <TextField
            label="Quantity to sell"
            type="number"
            fullWidth
            value={sellDialog.quantity}
            onChange={(e) => setSellDialog({
              ...sellDialog,
              quantity: Math.max(1, Math.min(parseInt(e.target.value) || 1, sellDialog.item?.quantity || 1))
            })}
            inputProps={{ min: 1, max: sellDialog.item?.quantity || 1, style: { fontSize: '16px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeSellDialog} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={sellItem}
            variant="contained"
            sx={{
              background: '#E53935',
              '&:hover': {
                background: '#C62828',
              },
            }}
          >
            Confirm Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={bulkImportDialog}
        onClose={() => setBulkImportDialog(false)}
        shopName={shopName}
        onImport={handleBulkImport}
      />

      {/* Bulk Edit Dialog */}
      <BulkEditDialog
        open={bulkEditDialog}
        onClose={() => setBulkEditDialog(false)}
        shopName={shopName}
        items={items}
        onUpdate={loadItems}
      />

      {/* Single Item Edit Dialog */}
      <EditItemDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, item: null })}
        item={editDialog.item}
        shopName={shopName}
        onUpdate={loadItems}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

InventorySpreadsheet.displayName = 'InventorySpreadsheet';

export default InventorySpreadsheet;
